import { useQuery } from "@tanstack/react-query";

import { PERMISSION_CATALOG_CACHE_KEY } from "./keys";

import { PermissionRules } from "@/features/roles/helpers/permission-rules";
import { api } from "@/lib/api-client";
import { isMockEnabled, simulateDelay } from "@/mock/config";
import { PERMISSION_CATALOG_MOCK } from "@/mock/permission-catalog";
import { useGlobalStore } from "@/stores/global";
import { Response } from "@/types/pagination";
import { PermissionCatalog } from "@/types/role";

// Outside the hook so `select` keeps one reference and builds the rules once.
const toRules = (catalog: PermissionCatalog) => new PermissionRules(catalog);

export const fetchPermissionCatalog = async (
  workspaceId: string | null,
): Promise<PermissionCatalog> => {
  if (isMockEnabled()) {
    await simulateDelay();

    return PERMISSION_CATALOG_MOCK;
  }

  const { data } = await api
    .get(`workspaces/${workspaceId}/roles/catalog`)
    .json<Response<PermissionCatalog>>();

  return data;
};

// The catalog is the same for every workspace and only changes with a deploy.
export const usePermissionRules = () => {
  const activeWorkspaceId = useGlobalStore((state) => state.activeWorkspaceId);

  return useQuery({
    queryKey: PERMISSION_CATALOG_CACHE_KEY,
    enabled: !!activeWorkspaceId,
    staleTime: Infinity,
    queryFn: () => fetchPermissionCatalog(activeWorkspaceId),
    select: toRules,
    retry: 2,
  });
};
